import * as CL from '@design-system/component-library';
import { useSelector } from 'react-redux';
import { paths } from '../../common/constants/pathVariables.js';
import { SubscriptionCategory } from '../../common/enums.js';
import { useTitle } from '../../common/hooks/useTitle.js';
import {
  durationOfContractMsg,
  idNumberMsg,
  internetSubscriptionsMsg,
  mobileBroadbandMsg,
  omaElisaForEmployeeMsg,
  pukCodeMsg,
  statusMsg,
  subscriptionIdMsg,
  subscriptionStatusMsg,
  subscriptionTypeMsg,
  t,
  technicalDataMsg,
} from '../../common/i18n/index.js';
import {
  AccordionsMain,
  TechnicalInformationBroadBand,
  TechnicalInformationMobile,
} from '../../common/utils/accordionUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils.js';
import {
  getSubscriptionStatusForEmployee,
  isMobileBroadbandSubscription,
  isNettiLiteSubscription,
} from '../../common/utils/subscriptionUtils.js';
import { SubscriptionType } from '../../generated/api/models.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { BreadCrumbs } from '../BreadCrumbs/index.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { Loading } from '../Loading/index.js';
import { Status } from '../Status/Status.js';
import { ButtonGroup, SubscriptionDetailsButtonType } from '../SubscriptionDetails/subscriptionDetailsButtons.js';
import { EoeAdditionalServicesAccordion } from './EoeAdditionalServicesAccordion.js';
import { SimCardDetails } from './EoeSubscriptionDetailsVoice.js';
import { parseMobileSubscriptionAttrs } from './eoeSubscriptionUtils.js';
import { getCommitmentText } from './eppSubscriptionUtils.js';
import type { SubscriptionId } from '../../common/constants/pathInterfaces.js';
import type { EmployeeSubscription, SubscriptionStatus } from '../../common/types/subscription.js';
import type { Subscription, SubscriptionAction } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';

import './EoeSubscriptionDetails.scss';

interface BroadbandSubscriptionDetailsContentProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
}

interface DetailsWrapperContentProps {
  subscription: EmployeeSubscription;
  subscriptionActions?: SubscriptionAction[];
  isMobileBroadband: boolean;
}

const BroadbandSubscriptionDetailsContent = ({
  subscription,
  subscriptionStatus,
}: BroadbandSubscriptionDetailsContentProps) => (
  <CL.Description
    className="of-employee-subscription-details__description"
    items={[
      {
        title: t.LROP(subscriptionStatusMsg),
        description: <Status color={subscriptionStatus.color} textAfterBadge={subscriptionStatus.text} />,
      },
      {
        title: t.YTH3(idNumberMsg),
        description: subscription.subscriptionDisplayId,
      },
      {
        title: t.CAA9(durationOfContractMsg),
        description: getCommitmentText(subscription),
      },
      {
        title: t.QH8P(subscriptionTypeMsg),
        description: subscription.subscriptionName,
      },
    ]}
  />
);

interface MobileBroadbandSubscriptionDetailsContentProps {
  subscription: EmployeeSubscription;
  subscriptionStatus: SubscriptionStatus;
}

const MobileBroadbandSubscriptionDetailsContent = ({
  subscription,
  subscriptionStatus,
}: MobileBroadbandSubscriptionDetailsContentProps) => (
  <CL.Description
    className="of-employee-subscription-details__description"
    items={[
      {
        title: t.ASQT(statusMsg),
        description: <Status color={subscriptionStatus.color} textAfterBadge={subscriptionStatus.text} />,
      },
      {
        title: t.K5TL(pukCodeMsg),
        description: parseMobileSubscriptionAttrs(subscription).puk1,
      },
      {
        title: t.YTH3(subscriptionIdMsg),
        description: subscription.subscriptionDisplayId,
      },
      {
        title: t.CAA9(durationOfContractMsg),
        description: getCommitmentText(subscription),
      },
      {
        title: t.QH8P(subscriptionTypeMsg),
        description: subscription.subscriptionName,
      },
    ]}
  />
);

const SubscriptionTechnicalData = ({ subscription }: { subscription: EmployeeSubscription }) => {
  const isMobileBroadbandSub = isMobileBroadbandSubscription(subscription);
  // Technical information is only shown for Netti Lite subscriptions on mobile broadband
  if (isMobileBroadbandSub && !isNettiLiteSubscription(subscription)) {
    return <></>;
  }

  return (
    <AccordionsMain
      accordions={[
        {
          headerName: t.L2MX(technicalDataMsg),
          id: 'technical-information',
          includedData: isMobileBroadbandSub ? (
            <TechnicalInformationMobile details={subscription?.details?.mobile} />
          ) : (
            <TechnicalInformationBroadBand details={subscription?.details?.broadband} />
          ),
          displayed: true,
        },
      ]}
    />
  );
};

const DetailsWrapperContent = ({ subscription, subscriptionActions }: DetailsWrapperContentProps) => {
  const subscriptionStatus = getSubscriptionStatusForEmployee(subscription, subscriptionActions);
  const isMobileBroadbandSub = isMobileBroadbandSubscription(subscription);

  return (
    <div>
      {isMobileBroadbandSub ? (
        <MobileBroadbandSubscriptionDetailsContent
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
        />
      ) : (
        <BroadbandSubscriptionDetailsContent subscription={subscription} subscriptionStatus={subscriptionStatus} />
      )}
      <ButtonGroup
        buttonTypes={[
          isMobileBroadbandSub
            ? SubscriptionDetailsButtonType.MOBILE_FIXER
            : SubscriptionDetailsButtonType.BROADBAND_FIXER,
        ]}
        portal="EOE"
        subscription={subscription}
      />
      {isMobileBroadbandSub && (
        <>
          <SimCardDetails
            subscription={subscription}
            subscriptionStatus={subscriptionStatus}
            onClickSimActivation={() => {}}
            onClickOrderSim={() => {}}
            actions={false}
          />
          <EoeAdditionalServicesAccordion subscription={subscription} category={SubscriptionCategory.BROADBAND} />
        </>
      )}
      <SubscriptionTechnicalData subscription={subscription} />
    </div>
  );
};

export const EoeSubscriptionDetailsBroadband = ({ subscriptionId }: SubscriptionId) => {
  const bbSubs = useSelector((state: State) => state.user?.authenticated?.subscriptions?.broadband, deepEqual);
  const subscriptionActions = useSelector(
    (state: State) => state.selfservice?.pendingSubscriptionActions?.items || [],
    deepEqual
  );
  const { imagesBaseUrl } = useConfig();
  const subscription = bbSubs?.find(s => s.subscriptionId === subscriptionId);
  const subscriptionDetailPath =
    subscription?.subscriptionType === SubscriptionType.MOBILE_BROADBAND
      ? `${t.EQKJ(mobileBroadbandMsg)} ${subscription?.details?.mobile?.mobileNumber}`
      : subscription?.subscriptionDisplayId;
  const getBreadCrumbPaths = () => [
    t.H6JV(omaElisaForEmployeeMsg),
    t.SEYV(internetSubscriptionsMsg),
    subscription && subscriptionDetailPath ? subscriptionDetailPath : '...',
  ];
  const isMobileBroadbandSub = isMobileBroadbandSubscription(subscription);

  const subscriptionContactName = bbSubs?.find(s => s.subscriptionId === subscriptionId)?.subscriptionContactName;

  // We cannot use breadcrumb path as page title for the MOBILEBROADBAND, as it contains PII (mobileNumber)
  useTitle(
    subscription?.subscriptionType === SubscriptionType.MOBILE_BROADBAND
      ? [`${t.EQKJ(mobileBroadbandMsg)} ${subscription?.subscriptionDisplayId}`]
      : getBreadCrumbPaths()
  );

  return (
    <DetailsWrapper
      classes={['of-employee-subscription-details']}
      detailsTop={
        <BreadCrumbs
          breadCrumbPaths={[
            { name: t.H6JV(omaElisaForEmployeeMsg), path: paths.EMPLOYEE_HOME },
            { name: t.SEYV(internetSubscriptionsMsg), path: paths.EMPLOYEE_SUBSCRIPTIONS },
            { name: subscription && subscriptionDetailPath ? subscriptionDetailPath : '...' },
          ]}
        />
      }
      id="employee-subscription"
      headingTop={isMobileBroadbandSub ? t.EQKJ(mobileBroadbandMsg) : t.VI0P('Broadband')}
      heading={
        (isMobileBroadbandSub && subscriptionContactName) ||
        subscription?.subscriptionUserFriendlyId ||
        subscription?.subscriptionPurposeOfUse ||
        ''
      }
      headingBottom={isMobileBroadbandSub ? formatPhoneNumber(subscription?.details?.mobile?.mobileNumber || '') : ''}
      heroPicto={isMobileBroadbandSub ? 'mobilebb' : 'fixedbb'}
      imagesBaseUrl={imagesBaseUrl}
    >
      {subscription ? (
        <DetailsWrapperContent
          subscription={subscription}
          subscriptionActions={subscriptionActions}
          isMobileBroadband={isMobileBroadbandSub}
        />
      ) : (
        <Loading />
      )}
    </DetailsWrapper>
  );
};
